import { useEffect, useMemo } from "react";
import type { TableColumn } from "./data-table";

// Cabeceras del esqueleto cuando la tabla aun no conoce sus columnas (p. ej.
// las colecciones: los campos llegan con la coleccion). Se usan las de la
// ultima visita a esa tabla (orden, visibilidad y anchos del usuario) o, la
// primera vez, columnas genericas con el encabezado en esqueleto.

type SkeletonColumn = Pick<TableColumn, "key" | "label" | "className">;

export const SKELETON_COLUMN_PREFIX = "__skeleton-";

const PLACEHOLDERS: TableColumn[] = Array.from({ length: 4 }, (_, i) => ({
  key: `${SKELETON_COLUMN_PREFIX}${i}`,
  label: "",
}));

const memory = new Map<string, SkeletonColumn[]>();
const storageKey = (tableId: string) => `dt-skeleton-columns:${tableId}`;

function readCached(tableId: string): SkeletonColumn[] | null {
  const hit = memory.get(tableId);
  if (hit) return hit;
  try {
    const raw = localStorage.getItem(storageKey(tableId));
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    if (
      Array.isArray(parsed) &&
      parsed.length > 0 &&
      parsed.every((c) => c && typeof c.key === "string" && typeof c.label === "string")
    ) {
      memory.set(tableId, parsed as SkeletonColumn[]);
      return parsed as SkeletonColumn[];
    }
  } catch {
    // Sin almacenamiento: columnas genericas.
  }
  return null;
}

function writeCached(tableId: string, columns: SkeletonColumn[]) {
  const json = JSON.stringify(columns);
  if (JSON.stringify(memory.get(tableId)) === json) return;
  memory.set(tableId, columns);
  try {
    localStorage.setItem(storageKey(tableId), json);
  } catch {
    // Cuota o almacenamiento bloqueado: queda solo en memoria.
  }
}

/**
 * Columnas para el esqueleto: `undefined` si las columnas reales ya se
 * conocen (`pending` falso); en ese caso se recuerdan para la proxima visita.
 */
export function useSkeletonColumns(
  tableId: string | undefined,
  columns: TableColumn[],
  pending: boolean | undefined,
): TableColumn[] | undefined {
  useEffect(() => {
    if (pending || !tableId || columns.length === 0) return;
    writeCached(
      tableId,
      columns.map(({ key, label, className }) => (className ? { key, label, className } : { key, label })),
    );
  }, [pending, tableId, columns]);

  return useMemo(() => {
    if (!pending) return undefined;
    return (tableId ? readCached(tableId) : null) ?? PLACEHOLDERS;
  }, [pending, tableId]);
}
